import { useState, useEffect, useCallback, useMemo } from "react";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  limit,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";

/**
 * Custom hook for personnel management operations
 */
export const usePersonnel = () => {
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cache management
  const [cache, setCache] = useState({
    data: [],
    lastFetch: null,
    isStale: false,
  });

  const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes - much longer cache

  // Cache validation
  const isCacheValid = useCallback(() => {
    if (!cache.lastFetch) return false;
    return Date.now() - cache.lastFetch < CACHE_DURATION && !cache.isStale;
  }, [cache.lastFetch, cache.isStale]);

  // Helper function to convert Firestore timestamps to readable dates
  const formatFirestoreDate = useCallback((timestamp) => {
    if (!timestamp) return "לא זמין";

    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString("he-IL");
    }

    if (timestamp instanceof Date) {
      return timestamp.toLocaleString("he-IL");
    }

    try {
      return new Date(timestamp).toLocaleString("he-IL");
    } catch (error) {
      return "לא זמין";
    }
  }, []);

  // Optimized real-time personnel listener with caching
  useEffect(() => {
    // Check cache first
    if (isCacheValid() && cache.data.length > 0) {
      setPersonnel(cache.data);
      setLoading(false);
      setError(null);
      return;
    }

    const personnelRef = collection(db, "personnel");
    // Load ALL personnel but with smart caching to reduce reads
    const q = query(personnelRef, orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        // Optimized data mapping with minimal processing
        const personnelData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            personalNumber: data.personalNumber,
            firstName: data.firstName,
            lastName: data.lastName,
            nickname: data.nickname ?? "",
            email: data.email || "",
            phone: data.phone,
            rank: data.rank,
            isAttached: data.isAttached,
            unitId: data.unitId,
            isSecret: data.isSecret,
            // Only format dates when needed for display
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
          };
        });

        // Update cache with optimized data
        setCache({
          data: personnelData,
          lastFetch: Date.now(),
          isStale: false,
        });

        setPersonnel(personnelData);
        setLoading(false);
        setError(null);
      },
      (error) => {
        console.error("Error fetching personnel:", error);
        setError("שגיאה בטעינת החיילים");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [formatFirestoreDate, isCacheValid, cache.data, cache.lastFetch]);

  // Add personnel
  const addPersonnel = async (personnelData) => {
    try {
      // Generate unique personnel ID
      const personnelId = `PERSONNEL_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      const newPersonnelData = {
        ...personnelData,
        personnelId,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const personnelRef = doc(db, "personnel", personnelId);
      await setDoc(personnelRef, newPersonnelData);

      // Invalidate cache
      setCache((prev) => ({ ...prev, isStale: true }));

      return { success: true };
    } catch (error) {
      console.error("Error adding personnel:", error);
      return { success: false, error: "שגיאה בהוספת חייל" };
    }
  };

  // Update personnel
  const updatePersonnel = async (personnelId, updateData) => {
    try {
      const personnelRef = doc(db, "personnel", personnelId);
      const finalUpdateData = {
        ...updateData,
        updatedAt: new Date(),
      };

      await updateDoc(personnelRef, finalUpdateData);

      // Invalidate cache
      setCache((prev) => ({ ...prev, isStale: true }));

      return { success: true };
    } catch (error) {
      console.error("Error updating personnel:", error);
      return { success: false, error: "שגיאה בעדכון חייל" };
    }
  };

  // Delete personnel
  const deletePersonnel = async (personnelId) => {
    try {
      await deleteDoc(doc(db, "personnel", personnelId));

      // Invalidate cache
      setCache((prev) => ({ ...prev, isStale: true }));

      return { success: true };
    } catch (error) {
      console.error("Error deleting personnel:", error);
      return { success: false, error: "שגיאה במחיקת חייל" };
    }
  };

  // Get unit name by ID (for display in table)
  const getUnitName = useCallback((unitId) => {
    if (!unitId) return "-";
    // This will be populated from the units collection
    // For now, return the unitId
    return unitId;
  }, []);

  return {
    personnel,
    loading,
    error,
    addPersonnel,
    updatePersonnel,
    deletePersonnel,
    getUnitName,
  };
};
